import React, { useEffect, useState, useCallback} from "react";
import Sidebar from "../../../layout/Sidebar";
import Header from "../../../layout/Header";
import Footer from "../../../layout/Footer";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { can } from "../../../utils/can";


export default function State() {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      isActive: true,
      website: false,
    },
  });

  const isActive = watch("isActive");

  const [success, setSuccess] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // Religion list state
  const [country, setCountry] = useState([]);
  const [editId, setEditId] = useState(null);
  const token = localStorage.getItem("adminToken");
  // cast list
  const [state, setState] = useState([]);

  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [listLoading, setListLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [activeSearch, setActiveSearch] = useState("");

  const PAGE_SIZE = 10;

  const API_URL = process.env.REACT_APP_BASE_URL_API || ""; // make sure no trailing slash

  const fetchCountry = useCallback(async () => {
  try {
    const url = `${API_URL.replace(/\/$/, "")}/api/front/country`;
    const response = await fetch(url, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const result = await response.json();
    if (response.ok) setCountry(result.data);
  } catch (err) {
    console.error("Error fetching country:", err);
    setCountry([]);
  }
}, [API_URL, token]);


  
   const fetchState = useCallback(
    async (pageNumber = 1, currentSearch = activeSearch) => {
      setListLoading(true);
      try {
        let url = `${API_URL.replace(/\/$/, "")}/api/admin/state?page=${pageNumber}&limit=${PAGE_SIZE}`;
        if (currentSearch && currentSearch.trim()) {
          url += `&search=${encodeURIComponent(currentSearch.trim())}`;
        }
        const response = await fetch(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const result = await response.json();
        if (response.ok) {
          setState(result.data);
          setTotalPages(result.totalPages || 1);
        }
      } catch (err) {
        console.error("Error fetching state:", err);
      } finally {
        setListLoading(false);
      }
    },
    [API_URL, token, PAGE_SIZE, activeSearch]
  );


  useEffect(() => {
    fetchCountry();
    fetchState(page, activeSearch);
  }, [page, fetchCountry, fetchState, activeSearch]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    setActiveSearch(searchInput);
    fetchState(1, searchInput);
  };

  const handleSearchReset = () => {
    setSearchInput("");
    setActiveSearch("");
    setPage(1);
    fetchState(1, "");
  };

  // form submit
  const onSubmit = async (data) => {
    if(can("state","create") === false){
          alert("You don't have permission.");
          return;
      }

    setSuccess("");
    setErrorMsg("");
    setLoading(true);


    try {
      const url = editId
        ? `${API_URL.replace(/\/$/, "")}/api/admin/state/update/${editId}`
        : `${API_URL.replace(/\/$/, "")}/api/admin/state/create`;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          country_id: data.country_id,
          name: data.name.trim(),
          status: data.isActive ? 'Active' : 'Deactive',
          website: data.isActive ? !!data.website : false,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess(editId ? "State updated successfully!" : "State added successfully!");
        setErrorMsg("");
        reset(); // clear form
        setEditId(null);
        fetchState(page, activeSearch);
      } else {
        setErrorMsg(result.message || "Failed to save state.");
      }
    } catch (err) {
      console.error("Error:", err);
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (state) => {
    if(can("state","edit") === false){
          alert("You don't have permission.");
          return;
      }
    setValue("country_id", state?.country_id?._id || "");
    setValue("name", state?.name || "");
    setValue("isActive", state?.status === "Active");
    setValue("website", !!state?.website);
    setEditId(state?._id || null); // store id for update
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleStatusToggle = async (item) => {
    if (can("state", "edit") === false) {
      alert("You don't have permission.");
      return;
    }

    const newStatus = item.status === "Active" ? "Deactive" : "Active";

    try {
      const response = await fetch(
        `${API_URL.replace(/\/$/, "")}/api/admin/state/update/${item._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            status: newStatus,
            ...(newStatus === "Deactive" ? { website: false } : {}),
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setState((prev) =>
          prev.map((row) =>
            row._id === item._id
              ? {
                  ...row,
                  status: newStatus,
                  website: newStatus === "Deactive" ? false : row.website,
                }
              : row
          )
        );
      } else {
        setSuccess(result.message || "Failed to update status.");
      }
    } catch (err) {
      console.error(err);
      setSuccess("Something went wrong!");
    }
  };

  const handleWebsiteToggle = async (item) => {
    if (can("state", "edit") === false) {
      alert("You don't have permission.");
      return;
    }

    if (item.status !== "Active") {
      alert("Inactive state cannot be enabled for Website. Please set Status to Active first.");
      return;
    }

    try {
      const response = await fetch(
        `${API_URL.replace(/\/$/, "")}/api/admin/state/update/${item._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            website: !item.website,
          }),
        }
      );

      const result = await response.json();

      if (response.ok) {
        setState((prev) =>
          prev.map((row) =>
            row._id === item._id
              ? { ...row, website: !item.website }
              : row
          )
        );
      } else {
        setSuccess(result.message || "Failed to update Website status.");
      }
    } catch (err) {
      console.error(err);
      setSuccess("Something went wrong!");
    }
  };


  // Delete religion
  const handleDelete = async (id) => {
    if(can("state","delete") === false){
          alert("You don't have permission.");
          return;
      }

    if (!window.confirm("Are you sure you want to delete this state?")) return;
    try {
      const response = await fetch(`${API_URL.replace(/\/$/, "")}/api/admin/state/delete/${id}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
      const result = await response.json();
      if (response.ok) {
        setSuccess("State deleted successfully!");
        fetchState(page);
      } else {
        setSuccess(result.message || "Failed to delete caste.");
      }
    } catch (err) {
      console.error(err);
      setSuccess("Something went wrong!");
    }
  };

  return (
    <div class="container body">
      <div class="main_container">
        <Sidebar />
        <Header pageTitle="State" />
        {/* page */}
        <div className="right_col" role="main">
          {/* top tiles */}
         
          {/* /top tiles */}
          {/* body card start */}
          <div className="x_panel">
            <div className="x_title">
              <h2>State Entry</h2>
              <ul className="nav navbar-right panel_toolbox" style={{ minWidth: 1 }}>
                <li>
                  <a className="collapse-link">
                    <i className="fa fa-chevron-up" />
                  </a>
                </li>

              </ul>
              <div className="clearfix" />
            </div>
            <div className="x_content">
              <div className="row">
                <div className="col-sm-12">
                  {/* filter start */}
                  <div className="filtercont">
                    <form onSubmit={handleSubmit(onSubmit)}>
                      <div className="row">
                        <div className="col-12 col-sm-4 pr-lg-0 mb-3">
                          <label>Select Country</label>
                          <select className={`form-select ${errors.country_id ? "is-invalid" : ""}`}
                            {...register("country_id", { required: "Please select a country" })}>
                            <option value="">Select</option>
                            {country.length > 0 &&
                              country.map((item) => (
                                <option key={item._id} value={item._id}>
                                  {item.name}
                                </option>
                              ))}

                          </select>
                          {errors.country_id && (
                            <div className="text-danger mt-1">{errors.country_id.message}</div>
                          )}
                        </div>
                        <div className="col-12" />
                        <div className="col-12 col-sm-4 pr-lg-0 mb-3">
                          <label>Enter State</label>
                          <input
                            type="text"
                            className={`form-control ${errors.name ? "is-invalid" : ""}`}
                            {...register("name", {
                              required: "Please enter state name",
                              minLength: { value: 2, message: "Minimum 2 characters" },
                            })}
                          />
                          {errors.name && (
                            <div className="text-danger mt-1">{errors.name.message}</div>
                          )}
                        </div>
                        <div className="col-12" />
                        <div className="col-12 col-sm-4 pr-lg-0">
                          <input type="checkbox" id="active" {...register("isActive")} />
                          <label htmlFor="active" className="form-label m-2">
                            All Platform Status
                          </label>
                        </div>
                        <div className="col-12 col-sm-4 pr-lg-0">
                          <input
                            type="checkbox"
                            id="website"
                            {...register("website")}
                            disabled={!isActive}
                          />
                          <label htmlFor="website" className="form-label m-2">
                            Website
                          </label>
                        </div>
                        <div className="col-12" />
                        <div className="col-12 col-sm-4 pr-lg-0 text-right">
                          <button type="submit" className="btn sitebtn" disabled={loading}>
                            {loading ? "Submitting..." : "Submit"}
                          </button>
                          {success && <div className="text-success mt-2">{success}</div>}
                          {errorMsg && <div className="text-danger mt-2">{errorMsg}</div>}
                        </div>
                      </div>
                    </form>
                  </div>
                  {/* filter end */}
                </div>
              </div>
            </div>
          </div>
          {/* bod card end */}
          {/* body card start */}
          <div className="x_panel">
            <div className="x_title">
              <h2>State List</h2>
              <ul className="nav navbar-right panel_toolbox" style={{ minWidth: 1 }}>
                <li>
                  <a className="collapse-link">
                    <i className="fa fa-chevron-up" />
                  </a>
                </li>

              </ul>
              <div className="clearfix" />
            </div>
            <div className="x_content">
              {/* Search Bar */}
              <div className="row mb-3">
                <div className="col-12 col-md-6 col-lg-4">
                  <form onSubmit={handleSearchSubmit}>
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search state..."
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                      />
                      <button className="btn sitebtn mb-0" type="submit" style={{ borderRadius: "0 4px 4px 0" }}>
                        <i className="fa fa-search" /> Search
                      </button>
                      {(activeSearch || searchInput) && (
                        <button
                          className="btn btn-secondary mb-0 ms-1 ml-1"
                          type="button"
                          onClick={handleSearchReset}
                        >
                          Reset
                        </button>
                      )}
                    </div>
                  </form>
                </div>
              </div>

              <div className="row">
                <div className="col-sm-12">
                  {listLoading ? (
                    <div>Loading...</div>
                  ) : (
                    <div className="table-responsive">
                      <table
                        id="datatable-checkbox"
                        className="table table-striped table-bordered bulk_action mytable"
                        style={{ width: "100%" }}
                        cellPadding={0}
                        cellSpacing={0}
                      >
                        <thead>
                          <tr>
                            <th style={{ width: 20 }}>S.No</th>
                            <th style={{ width: "" }}>Country</th>
                            <th style={{ width: "" }}>State</th>
                            <th style={{ width: 140 }}>All Platform Status</th>
                            <th style={{ width: 90 }}>Website</th>
                            <th style={{ width: 100 }}>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {state.length > 0 ? (
                            state.map((item, index) => (
                              <tr key={item._id}>
                                <td>{(page - 1) * PAGE_SIZE + index + 1}</td>
                                <td>{item.country_id?.name || "-"}</td>
                                <td>{item.name}</td>

                                <td>
                                  <div
                                    className="checkbox-wrapper-8"
                                    style={{ cursor: "pointer" }}
                                    onClick={() => handleStatusToggle(item)}
                                    title="Click to toggle All Platform Status"
                                  >
                                    <input
                                      className="tgl tgl-skewed"
                                      type="checkbox"
                                      checked={item.status === "Active"}
                                      readOnly
                                    />
                                    <label
                                      className="tgl-btn"
                                      data-tg-off="Inactive"
                                      data-tg-on="Active"
                                    />
                                  </div>
                                </td>
                                <td>
                                  <input
                                    type="checkbox"
                                    checked={item.status === "Active" && !!item.website}
                                    disabled={item.status !== "Active"}
                                    onChange={() => handleWebsiteToggle(item)}
                                    title={item.status !== "Active" ? "Activate state to enable" : "Show on Website"}
                                  />
                                </td>
                                <td className="p-0">
                                  <ul className="actionlist">
                                    <li>
                                      <Link title="Edit" onClick={(e) => {
                                              e.preventDefault();
                                              handleEdit(item);
                                            }}
                                      >
                                        <i className="fa fa-edit" />
                                      </Link>
                                    </li>
                                    <li>
                                      <Link title="Delete" onClick={() => handleDelete(item._id)}>
                                        <i className="fa fa-trash" />
                                      </Link>
                                    </li>
                                    <li></li>
                                  </ul>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan={6} className="text-center">
                                No country found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>

                      {/* Pagination */}
                      <div className="mt-3">
                        {Array.from({ length: totalPages }, (_, i) => (
                          <button
                            key={i}
                            className={`btn btn-sm mx-1 ${page === i + 1 ? "btn-primary" : "btn-light"}`}
                            onClick={() => setPage(i + 1)}
                          >
                            {i + 1}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          {/* bod card end */}
        </div>

        {/* page end */}
        <Footer />

      </div>
    </div>
  );
}